import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import {
  getAllBackgrounds,
  getBackgroundById,
  DEFAULT_BACKGROUND_ID,
  BACKGROUNDS as AVAILABLE_BACKGROUND_REGISTRY // Import the registry map for validation
} from '../backgrounds/BackgroundRegistry';

// Create Context
const BackgroundContext = createContext();

// Helper to validate background ID
const isValidBackgroundId = (id) => {
  return !!AVAILABLE_BACKGROUND_REGISTRY[id];
};

export const BackgroundProvider = ({ children }) => {
  // State for the list of available background metadata
  const [availableBackgrounds, setAvailableBackgrounds] = useState([]);
  
  // State for the currently selected background ID
  const [selectedBackgroundId, setSelectedBackgroundId] = useState(() => {
    try {
      const savedBackgroundId = localStorage.getItem('selectedBackgroundId');
      // Return saved ID if valid, otherwise default
      return isValidBackgroundId(savedBackgroundId) ? savedBackgroundId : DEFAULT_BACKGROUND_ID;
    } catch (error) {
      console.error("Error reading background ID from localStorage:", error);
      return DEFAULT_BACKGROUND_ID;
    }
  });

  // Effect to load available backgrounds on mount
  useEffect(() => {
    try {
      const backgrounds = getAllBackgrounds();
      setAvailableBackgrounds(backgrounds);
      console.log('BackgroundContext: Loaded available backgrounds');
      
      // Re-validate selected ID after loading backgrounds
      if (!isValidBackgroundId(selectedBackgroundId)) {
          console.warn(`BackgroundContext: Saved background ID "${selectedBackgroundId}" is invalid, resetting to default.`);
          setSelectedBackgroundId(DEFAULT_BACKGROUND_ID);
      }
    } catch (error) {
      console.error("BackgroundContext: Failed to load backgrounds:", error);
      setAvailableBackgrounds([]);
    }
  }, []); // Run only on mount

  // Effect to save selected background ID to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('selectedBackgroundId', selectedBackgroundId);
      console.log(`BackgroundContext: Saved selectedBackgroundId: ${selectedBackgroundId}`);
    } catch (error) {
      console.error("Error saving background ID to localStorage:", error);
    }
  }, [selectedBackgroundId]);

  // Wrapper for setting background ID to allow validation
  const wrappedSetSelectedBackgroundId = useCallback((newId) => {
      if (isValidBackgroundId(newId)) {
          setSelectedBackgroundId(newId);
      } else {
          console.error(`BackgroundContext: Attempted to set invalid background ID: ${newId}`);
      }
  }, []);

  // Memoize the context value
  const value = useMemo(() => {
    // Resolve the active background object (including component) based on the selected ID
    const activeBackgroundData = getBackgroundById(selectedBackgroundId);
    const activeBackgroundComponent = activeBackgroundData?.component || null;

    return {
      availableBackgrounds,
      selectedBackgroundId, // The ID (e.g., 'blank', 'tech')
      setSelectedBackgroundId: wrappedSetSelectedBackgroundId, // Setter function
      activeBackgroundData, // Full data object for the selected background (name, desc, icon, component)
      activeBackgroundComponent, // The actual component to render (or null)
    };
  }, [availableBackgrounds, selectedBackgroundId, wrappedSetSelectedBackgroundId]);

  return (
    <BackgroundContext.Provider value={value}>
      {children}
    </BackgroundContext.Provider>
  );
};

// Custom hook to use the context
export const useBackground = () => {
  const context = useContext(BackgroundContext);
  if (context === undefined) {
    throw new Error('useBackground must be used within a BackgroundProvider');
  }
  return context;
}; 